import { Injectable, inject, effect, untracked } from '@angular/core';
import { LoggerService } from '@core/logging';
import { TranslationService, type Locale } from './translation.service';
import { UserPreferencesStore, bcp47ToLocale, localeToBcp47 } from '@core/user-preferences';

/**
 * Synchronise la locale UI <-> backend.
 *
 * Hydratation du store vers le service de traduction :
 *  - Quand `UserPreferencesStore.isLoaded()` passe à `true`, on lit
 *    `preferredLocale` et on l'applique à `TranslationService` (le serveur
 *    gagne au load pour cohérence cross-device).
 *
 * Push UI -> backend :
 *  - À chaque changement de `TranslationService.locale()` après hydratation,
 *    on PATCH `/me/preferences` (fire-and-forget, log on error).
 *
 * Le service est instancié au boot via `provideAppInitializer` ou par simple
 * injection — l'effect démarre à la création.
 */
@Injectable({ providedIn: 'root' })
export class LocaleSyncService {
  private readonly translation = inject(TranslationService);
  private readonly preferencesStore = inject(UserPreferencesStore);
  private readonly logger = inject(LoggerService);

  /** Indique si la première hydratation backend a déjà eu lieu (anti-boucle). */
  private hydrated = false;

  constructor() {
    // Hydratation : backend -> UI (one-way au premier load).
    effect(() => {
      if (!this.preferencesStore.isLoaded()) return;
      if (this.hydrated) return;

      const bcp47 = this.preferencesStore.locale();
      const next = bcp47ToLocale(bcp47);
      if (next && next !== untracked(this.translation.locale)) {
        this.translation.setLocale(next);
      }
      this.hydrated = true;
    });

    // Push UI -> backend (uniquement après hydratation pour éviter l'écho).
    effect(() => {
      const current = this.translation.locale();
      if (!this.hydrated) return;
      if (!this.preferencesStore.isLoaded()) return;

      const currentBcp47 = untracked(this.preferencesStore.locale);
      const nextBcp47 = localeToBcp47(current);
      if (currentBcp47 === nextBcp47) return;

      void this.preferencesStore
        .updatePreferences({ preferredLocale: nextBcp47 })
        .catch(err => this.logger.warn('LocaleSyncService', 'Failed to sync locale', { err }));
    });
  }

  /**
   * Reset interne — appelé au logout pour repartir d'un état propre quand un
   * autre user se logge.
   */
  reset(): void {
    this.hydrated = false;
  }

  /** Force la résolution de la locale UI active (utile pour debug/tests). */
  get currentLocale(): Locale {
    return this.translation.locale();
  }
}
